import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@appcharge/shared-ui'
import { Divider, Typography } from '@mui/material';
import { US_STATES } from 'constants/constants';
import countries from 'i18n-iso-countries';
import { Base64 } from 'js-base64';

import { AcAdvancedTextInput } from 'components/AcAdvancedTextInput/AcAdvancedInput';
import ActionCell from 'components/DataTableComponent/components/ActionCell';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';

import { CheckoutThemeFormValues } from './types';

export const CheckoutLegalText = memo(({ values, setFieldValue, defaultValues }: {
    values: CheckoutThemeFormValues,
    setFieldValue: (field: string, value: any) => void,
    defaultValues: CheckoutThemeFormValues
}) => {
    countries.registerLocale(require('i18n-iso-countries/langs/en.json'));
    const countriesList = useMemo(() => countries.getNames('en'), []);

    const [selectedCountryCode, setSelectedCountryCode] = useState(Object.keys(countriesList)[0] ?? '');
    const [selectedStateCode, setSelectedStateCode] = useState<string>(Object.keys(US_STATES)[0] ?? '');
    const [currentText, setCurrentText] = useState('');

    const isUnitedStates = selectedCountryCode.toUpperCase() === 'US';

    const dropdownOptions = useMemo(() => Object.entries(countriesList).map(([key, value]) => ({
        label: value,
        value: key,
    })), [countriesList]);

    const stateDropdownOptions = useMemo(() =>
        Object.entries(US_STATES).map(([key, value]) => ({
            label: value,
            value: key,
        })), []);

    useEffect(() => {
        let value = '';
        if (isUnitedStates) {
            value = (values.footerConfiguration?.[selectedCountryCode] as { [stateCode: string]: { richText: string } })?.[selectedStateCode]?.richText ?? '';
        } else {
            value = (values.footerConfiguration?.[selectedCountryCode] as { richText: string })?.richText ?? '';
        }
        setCurrentText(Base64.decode(value));
    }, [selectedCountryCode, selectedStateCode, isUnitedStates, values.footerConfiguration]);

    const handleSave = () => {
        const encodedText = Base64.encode(currentText);

        if (isUnitedStates) {
            setFieldValue(`footerConfiguration.${selectedCountryCode}.${selectedStateCode}.richText`, encodedText);
        } else {
            setFieldValue(`footerConfiguration.${selectedCountryCode}.richText`, encodedText);
        }
    };

    const handleEdit = useCallback((countryCode: string, stateCode?: string) => {
        setSelectedCountryCode(countryCode);
        if (stateCode) {
            setSelectedStateCode(stateCode);
        }
        setTimeout(() => {
            if (stateCode) {
                const value = (values.footerConfiguration?.[countryCode] as { [stateCode: string]: { richText: string } })?.[stateCode]?.richText ?? '';
                setCurrentText(Base64.decode(value));
            } else {
                const value = (values.footerConfiguration?.[countryCode] as { richText: string })?.richText ?? '';
                setCurrentText(Base64.decode(value));
            }
        }, 0);
    }, [values.footerConfiguration]);

    const handleDelete = useCallback((countryCode: string, stateCode?: string) => {
        if (countryCode === 'DEFAULT') return;

        if (stateCode) {
            const usConfig = values.footerConfiguration?.[countryCode] as { [stateCode: string]: { richText: string } } | undefined;
            if (usConfig) {
                const { [stateCode]: _, ...remaining } = usConfig;
                if (Object.keys(remaining).length === 0) {
                    setFieldValue(`footerConfiguration.${countryCode}`, undefined);
                } else {
                    setFieldValue(`footerConfiguration.${countryCode}.${stateCode}`, undefined);
                }
            }
        } else {
            setFieldValue(`footerConfiguration.${countryCode}`, undefined);
        }
    }, [values.footerConfiguration, setFieldValue]);

    const listItems = useMemo(() => {
        return Object.entries(values.footerConfiguration || {}).filter(([key]) => key !== 'DEFAULT').map(([key, value]) => {
            if (key.toUpperCase() === 'US' && typeof value === 'object' && !('richText' in value)) {
                return Object.keys(value).map((stateKey) => (
                    <div className="flex gap-1 items-center justify-between" key={stateKey}>
                        <div>{US_STATES[stateKey as keyof typeof US_STATES]}</div>
                        <ActionCell
                            rowData={value}
                            actions={[
                                { label: 'Edit', handler: () => handleEdit(key, stateKey) },
                                { label: 'Delete', handler: () => handleDelete(key, stateKey) },
                            ]}
                        />
                    </div>
                ));
            } else {
                return (
                    <div className="flex gap-1 items-center justify-between" key={key}>
                        <div>{countriesList[key as keyof typeof countriesList]}</div>
                        <ActionCell
                            rowData={value}
                            actions={[
                                { label: 'Edit', handler: () => handleEdit(key) },
                                { label: 'Delete', handler: () => handleDelete(key) },
                            ]}
                        />
                    </div>
                );
            }
        });
    }, [values.footerConfiguration, countriesList, handleEdit, handleDelete]);

    const hasExistingValue = useMemo(() => {
        if (isUnitedStates) {
            return (values.footerConfiguration?.[selectedCountryCode] as { [stateCode: string]: { richText: string } })?.[selectedStateCode]?.richText !== undefined;
        } else {
            return (values.footerConfiguration?.[selectedCountryCode] as { richText: string })?.richText !== undefined;
        }
    }, [selectedCountryCode, selectedStateCode, isUnitedStates, values.footerConfiguration]);

    return (
        <div className="flex flex-col gap-4 mt-6 border border-base-border rounded-lg p-4">
            <div className="flex flex-col gap-4 base-foreground text-xl font-bold">
                <Typography variant="h6">Legal Text</Typography>
            </div>
            <div className="flex flex-col gap-2 mt-6">
                <div>
                    <div className="text-base-foreground font-bold">General Footer Legal Text</div>
                </div>
                <AcAdvancedTextInput
                    value={Base64.decode(values.footerConfiguration?.DEFAULT?.richText as string ?? defaultValues.footerConfiguration.DEFAULT.richText as string)}
                    setValue={(value) => setFieldValue('footerConfiguration.DEFAULT.richText', Base64.encode(value))}
                    error={false}
                    enableColorPicker={true}
                    handleOnEmpty={() => {
                        setFieldValue('footerConfiguration.DEFAULT.richText', '');
                    }}
                />
                <Divider />
                <div className="flex flex-col gap-4 w-full z-10 mt-4">
                    <div className="flex flex-col gap-2">
                        <div className="text-base-foreground font-bold">Localized Legal Text</div>
                    </div>
                    <div className="flex flex-col gap-2 w-full">
                        <InputSelectDropdown
                            dataTestId="country-select"
                            label="Country"
                            options={dropdownOptions}
                            defaultSelected={dropdownOptions.find(option => option.value === selectedCountryCode)}
                            onOptionChange={(value: string) => {
                                setSelectedCountryCode(value);
                            }}
                            buttonDataTestId="country-select-button"
                            listDataTestId="country-select-list"
                        />

                        {isUnitedStates && (
                            <InputSelectDropdown
                                label="State"
                                options={stateDropdownOptions}
                                defaultSelected={stateDropdownOptions.find(option => option.value === selectedStateCode)}
                                onOptionChange={(value: string) => {
                                    setSelectedStateCode(value);
                                }}
                                buttonDataTestId="state-select-button"
                                listDataTestId="state-select-list"
                            />
                        )}

                        <AcAdvancedTextInput
                            value={currentText}
                            setValue={setCurrentText}
                            error={false}
                            enableColorPicker={true}
                        />

                        <Button onClick={handleSave} type="button">
                            {hasExistingValue ? 'Update' : 'Add'}
                        </Button>

                        <Divider />
                        <div className="flex flex-col gap-1 max-h-[300px] overflow-y-auto">
                            {listItems}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});
